/**
 * Analytics tracker for Sites/Accounts module
 * Matches iOS AccountsTracker.swift exactly
 */

import AnalyticsManager from '../AnalyticsManager'
import { ScreenName, ButtonName, ButtonLocation, ListName } from '../ScreenName'
import { AnalyticsParameters } from '../AnalyticsParameters'
import { AnalyticsEvent } from '../AnalyticsEvents'

/**
 * Protocol/interface for SitesTracker
 * Matches iOS AccountsTrackerProtocol
 */
export interface SitesTrackerProtocol {
  trackOpenScreen(): void
  trackRefreshListClicked(): void
  trackListLoaded(loadTrigger: string, success: boolean, itemsCount: number): void
  trackSortButtonClicked(): void
  trackAddSiteButtonClicked(): void
  trackSearchPerformed(resultsCount: number): void
  trackSiteSelected(siteId: number, partyId: number | null): void
  trackIssueItemsClicked(siteId: number): void
  trackReceiveItemsClicked(siteId: number): void
  trackLostItemsClicked(siteId: number): void
  trackMoreActionsClicked(siteId: number): void
  trackWhatsAppAction(partyId: number): void
  trackCallAction(partyId: number): void
  trackNewSiteForPartyClicked(siteId: number | null, partyId: number | null): void
  trackSortApplied(sortOption: string): void
  trackFilterApplied(filterOption: string): void
}

/**
 * Sites tracker implementation
 */
class SitesTracker implements SitesTrackerProtocol {
  private analyticsManager: AnalyticsManager

  constructor(analyticsManager: AnalyticsManager = AnalyticsManager.shared) {
    this.analyticsManager = analyticsManager
  }

  trackOpenScreen(): void {
    this.analyticsManager.trackScreenView(ScreenName.SITES)
  }

  trackListLoaded(loadTrigger: string, success: boolean, itemsCount: number): void {
    this.analyticsManager.trackListLoaded(
      loadTrigger,
      ListName.SITES,
      ScreenName.SITES,
      success,
      itemsCount
    )
  }

  trackSortButtonClicked(): void {
    this.analyticsManager.trackButtonClick(
      ButtonName.SITES_SORT,
      ButtonLocation.HEADER,
      ScreenName.SITES
    )
  }

  trackAddSiteButtonClicked(): void {
    this.analyticsManager.trackButtonClick(
      ButtonName.ADD_SITE,
      ButtonLocation.HEADER,
      ScreenName.SITES
    )
  }

  trackSearchPerformed(resultsCount: number): void {
    this.analyticsManager.trackEvent(AnalyticsEvent.SITE_SEARCH_PERFORMED, ScreenName.SITES, {
      [AnalyticsParameters.searchResultsCount]: resultsCount,
    })
  }

  trackSiteSelected(siteId: number, partyId: number | null): void {
    const parameters: Record<string, any> = {
      [AnalyticsParameters.siteId]: siteId,
    }

    if (partyId !== null && partyId !== undefined) {
      parameters[AnalyticsParameters.partyId] = partyId
    }

    this.analyticsManager.trackEvent(AnalyticsEvent.SITE_SELECTED, ScreenName.SITES, parameters)
  }

  trackRefreshListClicked(): void {
    this.analyticsManager.trackButtonClick(
      ButtonName.SITES_REFRESH,
      ButtonLocation.TOP,
      ScreenName.SITES
    )
  }

  trackIssueItemsClicked(siteId: number): void {
    const parameters: Record<string, any> = {
      [AnalyticsParameters.siteId]: siteId,
    }

    this.analyticsManager.trackButtonClick(
      ButtonName.SITE_ISSUE_ITEMS,
      ButtonLocation.LIST_ITEM,
      ScreenName.SITES,
      parameters
    )
  }

  trackReceiveItemsClicked(siteId: number): void {
    const parameters: Record<string, any> = {
      [AnalyticsParameters.siteId]: siteId,
    }

    this.analyticsManager.trackButtonClick(
      ButtonName.SITE_RECEIVE_ITEMS,
      ButtonLocation.LIST_ITEM,
      ScreenName.SITES,
      parameters
    )
  }

  trackLostItemsClicked(siteId: number): void {
    const parameters: Record<string, any> = {
      [AnalyticsParameters.siteId]: siteId,
    }

    this.analyticsManager.trackButtonClick(
      ButtonName.SITE_LOST_ITEMS,
      ButtonLocation.LIST_ITEM,
      ScreenName.SITES,
      parameters
    )
  }

  trackMoreActionsClicked(siteId: number): void {
    const parameters: Record<string, any> = {
      [AnalyticsParameters.siteId]: siteId,
    }

    this.analyticsManager.trackButtonClick(
      ButtonName.SITE_MORE_ACTIONS,
      ButtonLocation.LIST_ITEM,
      ScreenName.SITES,
      parameters
    )
  }

  trackWhatsAppAction(partyId: number): void {
    this.analyticsManager.trackButtonClick(
      ButtonName.PARTY_WHATSAPP,
      ButtonLocation.HEADER,
      ScreenName.SITES,
      {
        [AnalyticsParameters.partyId]: partyId,
      }
    )
  }

  trackCallAction(partyId: number): void {
    this.analyticsManager.trackButtonClick(
      ButtonName.PARTY_CALL,
      ButtonLocation.HEADER,
      ScreenName.SITES,
      {
        [AnalyticsParameters.partyId]: partyId,
      }
    )
  }

  trackNewSiteForPartyClicked(siteId: number | null, partyId: number | null): void {
    const parameters: Record<string, any> = {}

    if (partyId !== null && partyId !== undefined) {
      parameters[AnalyticsParameters.partyId] = partyId
    }

    if (siteId !== null && siteId !== undefined) {
      parameters[AnalyticsParameters.siteId] = siteId
    }

    this.analyticsManager.trackButtonClick(
      ButtonName.PARTY_NEW_SITE,
      ButtonLocation.PRIMARY,
      ScreenName.SITES,
      parameters
    )
  }

  trackSortApplied(sortOption: string): void {
    this.analyticsManager.trackEvent(AnalyticsEvent.SITE_SORT_APPLIED, ScreenName.SITES, {
      [AnalyticsParameters.sortOption]: sortOption,
    })
  }

  trackFilterApplied(filterOption: string): void {
    this.analyticsManager.trackEvent(AnalyticsEvent.SITE_FILTER_APPLIED, ScreenName.SITES, {
      [AnalyticsParameters.filterOption]: filterOption,
    })
  }
}

export default SitesTracker

